import type { ButtonHTMLAttributes, ReactNode } from "react";

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  children: ReactNode;
  type?: "button" | "submit";
  variant?: "primary" | "secondary" | "ghost";
  className?: string;
}

export function Button({
  children,
  onClick,
  type = "button",
  variant = "primary",
  className = "",
  disabled,
  ...rest
}: ButtonProps) {
  const variantClass =
    variant === "primary"
      ? "pr-btn-primary"
      : variant === "secondary"
        ? "pr-btn-secondary"
        : "bg-transparent border-0 text-[var(--pr-text-muted)] hover:text-[var(--pr-text)] hover:bg-[rgba(164,214,76,0.05)]";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      {...rest}
      className={`inline-flex min-h-12 items-center justify-center rounded-button px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${variantClass} ${className}`}
    >
      {children}
    </button>
  );
}

interface CardProps {
  children: ReactNode;
  className?: string;
  flat?: boolean;
}

export function Card({ children, className = "", flat = false }: CardProps) {
  return <section className={`${flat ? "pr-card-flat" : "pr-card"} p-4 ${className}`}>{children}</section>;
}

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className = "" }: SkeletonProps) {
  return <div className={`pr-skeleton rounded-lg ${className}`} />;
}

interface EmptyStateProps {
  title: string;
  subtitle: string;
  action?: ReactNode;
  image?: string;
}

export function EmptyState({ title, subtitle, action, image }: EmptyStateProps) {
  return (
    <Card className="text-center">
      {image ? (
        <img src={image} alt="" className="mx-auto mb-4 h-36 w-full rounded-lg object-cover opacity-90" />
      ) : null}
      <h3 className="pr-display text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-[var(--pr-text-muted)]">{subtitle}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </Card>
  );
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}

export function PageHeader({ title, subtitle, right }: PageHeaderProps) {
  return (
    <header className="mb-4 flex items-start justify-between gap-4">
      <div>
        <h1 className="pr-display text-2xl font-semibold sm:text-3xl">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-[var(--pr-text-muted)]">{subtitle}</p> : null}
      </div>
      {right ? <div className="shrink-0">{right}</div> : null}
    </header>
  );
}

interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
}

export function ErrorState({ message, onRetry }: ErrorStateProps) {
  return (
    <Card>
      <h3 className="text-base font-semibold">Something went wrong</h3>
      <p className="mt-2 text-sm text-[var(--pr-text-muted)]">{message}</p>
      {onRetry ? (
        <Button className="mt-4" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </Card>
  );
}

interface FieldProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  type?: string;
}

export function TextField({ label, value, placeholder, onChange, type = "text" }: FieldProps) {
  return (
    <label className="flex flex-col gap-2 text-sm">
      <span className="pr-label">{label}</span>
      <input
        value={value}
        type={type}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-container)] px-3 py-2 text-sm text-[var(--pr-text)] outline-none ring-[var(--pr-accent)] transition focus:ring-1"
      />
    </label>
  );
}

interface SegmentOption<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
}

export function SegmentedControl<T extends string>({ value, options, onChange }: SegmentedControlProps<T>) {
  return (
    <div
      className="grid w-full rounded-lg border border-[var(--pr-border)] bg-[var(--pr-surface-container-low)] p-1"
      style={{ gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`rounded px-3 py-2 text-sm font-medium transition ${
              isActive ? "bg-[var(--pr-surface-container)] shadow-card" : "text-[var(--pr-text-muted)]"
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

interface PillProps {
  label: string;
  tone?: "default" | "accent";
}

export function Pill({ label, tone = "default" }: PillProps) {
  const className =
    tone === "accent"
      ? "border-[rgba(164,214,76,0.3)] bg-[rgba(164,214,76,0.1)] text-[var(--pr-accent)]"
      : "border-[var(--pr-border)] bg-[var(--pr-surface-container)] text-[var(--pr-text-muted)]";

  return <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${className}`}>{label}</span>;
}
